import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';

// Listed in `PERSISTENT_KEYS` in storageUtils, so it survives the `beforeunload`
// wipe on refresh but is cleared by `clearVoteMateStorageOnLogout` on sign-out.
const STORAGE_KEY = 'votemate_expanded_contests';

type ExpandedMap = Record<string, boolean>;

const readFromStorage = (): ExpandedMap => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return {};
    }
    const result: ExpandedMap = {};
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof value === 'boolean') {
        result[key] = value;
      }
    }
    return result;
  } catch {
    return {};
  }
};

/**
 * Per-contest expansion state. Drop-in replacement for `useState<ExpandedMap>({})`
 * that persists across refresh under `votemate_expanded_contests`, paired with
 * `useLikedCandidates` and `useElectionFilters` so a user's full ballot shape
 * (likes, filters, collapses) is restored when they return.
 */
const useExpandedContests = (): [ExpandedMap, Dispatch<SetStateAction<ExpandedMap>>] => {
  const [expanded, setExpanded] = useState<ExpandedMap>(readFromStorage);
  // Skip the no-op write on first render — the state was just hydrated from
  // localStorage, so persisting it back is pointless and writes `{}` every page
  // load for users who've never used the feature.
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(expanded));
    } catch {
      // localStorage may be full / disabled (private mode, quota) — fail silently.
    }
  }, [expanded]);

  return [expanded, setExpanded];
};

export default useExpandedContests;
